import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getPlayerStats, getPlayerTotal, getPlayerHistory, getSkinUrl, getBodyUrl } from '../api';
import StatsChart from '../components/StatsChart';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';

export default function PlayerProfile() {
  const { username } = useParams();
  const navigate = useNavigate();
  const [stats, setStats] = useState([]);
  const [total, setTotal] = useState(null);
  const [history, setHistory] = useState([]);
  const [serverFilter, setServerFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [statsData, totalData, historyData] = await Promise.all([
        getPlayerStats(username),
        getPlayerTotal(username),
        getPlayerHistory(username),
      ]);
      setStats(statsData || []);
      setTotal(totalData);
      setHistory(historyData?.history || []);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Jugador "' + username + '" no encontrado');
      } else {
        setError(err.message || 'Error cargando perfil');
      }
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, [username]);

  if (loading) return <LoadingSpinner text={'Buscando a ' + username + '...'} />;
  if (error) return (
    <div className="space-y-4">
      <button onClick={() => navigate(-1)} className="text-mc-accent text-sm hover:underline">&larr; Volver</button>
      <ErrorMessage message={error} onRetry={fetchData} />
    </div>
  );

  const isPremium = stats.length > 0 && stats[0].premium;
  const maxBlocks = Math.max(...stats.map((s) => s.blocks_mined), 1);
  const servers = stats.map(s => s.server_name);

  return (
    <div className="space-y-8">
      <button onClick={() => navigate(-1)} className="text-mc-accent text-sm hover:underline">&larr; Volver</button>

      {/* Profile header */}
      <div className="mc-border rounded-2xl p-8 bg-mc-card">
        <div className="flex flex-col md:flex-row items-center gap-6">
          <div className="relative">
            <img
              src={getBodyUrl(username)}
              alt={username}
              className="h-32"
              style={{ imageRendering: 'pixelated' }}
            />
          </div>

          <div className="text-center md:text-left flex-1">
            <div className="flex items-center gap-3 justify-center md:justify-start">
              <h1 className="mc-title text-mc-text text-2xl">{username}</h1>
              {isPremium && (
                <span className="px-2 py-0.5 bg-mc-gold/20 text-mc-gold rounded text-xs font-bold">PREMIUM</span>
              )}
            </div>
            <div className="mt-4 flex flex-wrap gap-4 justify-center md:justify-start">
              <div className="mc-border rounded-xl px-5 py-3 bg-mc-darker text-center">
                <p className="text-mc-accent font-bold text-2xl">{total?.total_blocks_mined?.toLocaleString('es-DO') || '0'}</p>
                <p className="text-mc-muted text-xs mt-1">Bloques totales</p>
              </div>
              <div className="mc-border rounded-xl px-5 py-3 bg-mc-darker text-center">
                <p className="text-mc-diamond font-bold text-2xl">{stats.length}</p>
                <p className="text-mc-muted text-xs mt-1">Servidores</p>
              </div>
            </div>
          </div>

          <img
            src={getSkinUrl(username)}
            alt=""
            className="w-20 h-20 rounded-xl hidden md:block"
            style={{ imageRendering: 'pixelated' }}
          />
        </div>
      </div>

      {/* History Chart */}
      {history.length > 0 && (
        <section className="mc-border rounded-2xl p-6 bg-mc-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="mc-title text-mc-diamond text-lg">📈 Progreso de Minería</h2>
            {servers.length > 1 && (
              <select
                value={serverFilter}
                onChange={(e) => setServerFilter(e.target.value)}
                className="bg-mc-darker border border-mc-border rounded-lg px-3 py-1.5 text-sm text-mc-text focus:outline-none focus:border-mc-accent"
              >
                <option value="">Todos los servidores</option>
                {servers.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            )}
          </div>
          <StatsChart history={history} serverFilter={serverFilter} />
        </section>
      )}

      {/* Per-server breakdown */}
      <section>
        <h2 className="mc-title text-mc-gold text-lg mb-4">📊 Estadísticas por Servidor</h2>
        <div className="space-y-3">
          {stats
            .sort((a, b) => b.blocks_mined - a.blocks_mined)
            .map((s, idx) => (
              <div key={s.server_name} className="mc-border rounded-xl p-5 bg-mc-card">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <span className="text-mc-muted font-mono text-sm">#{idx + 1}</span>
                    <div>
                      <p className="text-mc-text font-semibold">{s.server_name}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-mc-accent font-bold text-lg">{s.blocks_mined?.toLocaleString('es-DO')}</p>
                    <p className="text-mc-muted text-xs">bloques</p>
                  </div>
                </div>
                {/* Bar */}
                <div className="h-2 bg-mc-darker rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-mc-accent to-mc-diamond rounded-full transition-all duration-500"
                    style={{ width: (s.blocks_mined / maxBlocks) * 100 + '%' }}
                  />
                </div>
                <div className="text-right mt-1">
                  <span className="text-mc-muted text-xs">
                    {((s.blocks_mined / (total?.total_blocks_mined || 1)) * 100).toFixed(1)}% del total
                  </span>
                </div>
              </div>
            ))}
        </div>
      </section>
    </div>
  );
}
